import { saveAs } from 'file-saver';
import {
  buildClientCohortView,
  buildClientCohortViewFromDealIrrModel,
  computeClientCohortDisplayEquity,
  computeClientCohortDisplayTotals,
  getClientCohortDisplayColumns,
  selectClientCohortDisplayValues,
} from './buildClientCohortView';

const BLACK = '111111';
const WHITE = 'FFFFFF';
const GREY = '606060';
const BODY = '222222';
const GREEN = '1B7A3D';
const RED = 'B02A2A';
const LINE = 'DDDDDD';
const LITE = 'F4F4F4';
const GREENBG = 'E4F1E8';
const MUTE = 'D2D2D2';
const FAINT = 'FBFBFB';
const NAVY = '122A3A';
const TOTCELL = 'ECECEC';
const FONT = 'Arial';
const EMPTY = '–';

function fmtThousands(value) {
  const amount = Number(value) || 0;
  const thousands = Math.round(amount / 1000);
  if (thousands === 0) {
    return EMPTY;
  }
  const formatted = Math.abs(thousands).toLocaleString('en-US');
  return amount < 0 ? `(${formatted})` : formatted;
}

function fmtKDollar(value) {
  const thousands = Math.round((Number(value) || 0) / 1000);
  return `$${thousands.toLocaleString('en-US')}K`;
}

function fmtRoasPct(value) {
  const pct = Number(value) || 0;
  if (pct === 0) {
    return EMPTY;
  }
  return `${Math.round(pct)}%`;
}

function fmtMultiple(value) {
  if (value == null || !Number.isFinite(value)) {
    return EMPTY;
  }
  return `${value.toFixed(1)}x`;
}

function fmtCappedReturnPct(value) {
  if (value == null || !Number.isFinite(value)) {
    return EMPTY;
  }
  return `${value.toFixed(2)}% / mo`;
}

function cellFontColor(kind, numeric, text) {
  if (kind === 'pct') {
    return GREY;
  }
  if (kind === 'out') {
    return text === EMPTY ? BODY : RED;
  }
  if (kind === 'in') {
    return text === EMPTY ? BODY : GREEN;
  }
  if (kind === 'cum') {
    return GREEN;
  }
  if (kind === 'net') {
    if (numeric < 0) {
      return RED;
    }
    if (numeric > 0) {
      return GREEN;
    }
    return BODY;
  }
  return BODY;
}

/**
 * Builds styled pptxgenjs table rows from the client cohort view.
 * Returns header + 7 data rows; each cell is { text, options }.
 */
export function buildClientCohortSlideTableRows(view) {
  const fundingPctLabel = Math.round(view.topKpis.twelveFundingPct);
  const revSharePctLabel = Math.round(view.topKpis.twelveRevSharePct);
  const displayColumns = getClientCohortDisplayColumns(view.monthCount);
  const displayTotals = computeClientCohortDisplayTotals(view, displayColumns);

  const rowDefs = [
    { label: 'ROAS %', values: view.rows.roasPct, total: displayTotals.roasPct, kind: 'pct' },
    { label: 'Cohort revenue', values: view.rows.cohortRevenue, total: displayTotals.cohortRevenue, kind: 'rev' },
    { label: `Twelve funds you (${fundingPctLabel}%)`, values: view.rows.twelveFundsYou, total: displayTotals.twelveFundsYou, kind: 'in' },
    {
      label: 'Customer pays supplier (net 30)',
      values: view.rows.customerRepaysSupplier,
      total: displayTotals.customerRepaysSupplier,
      kind: 'out',
    },
    {
      label: `Customer repays Twelve (${revSharePctLabel}%)`,
      values: view.rows.customerRepaysTwelve,
      total: displayTotals.customerRepaysTwelve,
      kind: 'out',
    },
    {
      label: 'Customer net cash (month)',
      values: view.rows.customerNetCash,
      total: displayTotals.customerNetCash,
      kind: 'net',
    },
    {
      label: 'Customer cash position (cum.)',
      values: view.rows.customerCashPosition,
      total: displayTotals.customerCashPosition,
      kind: 'cum',
    },
  ];

  const fmtVal = (kind, value) => (kind === 'pct' ? fmtRoasPct(value) : fmtThousands(value));

  const headerOpts = (align) => ({
    fill: { color: BLACK },
    color: WHITE,
    bold: true,
    align,
    valign: 'middle',
    fontFace: FONT,
    fontSize: 10.5,
  });

  const header = [
    { text: '', options: headerOpts('left') },
    ...displayColumns.map((col) => ({ text: col.header, options: headerOpts('center') })),
    { text: 'Total', options: headerOpts('center') },
  ];

  const body = rowDefs.map((row, idx) => {
    const oneBased = idx + 1;
    const cum = row.kind === 'cum';
    const net = row.kind === 'net';
    const pct = row.kind === 'pct';
    const stripeFill = oneBased % 2 ? FAINT : LITE;

    const labelCell = {
      text: row.label,
      options: {
        fill: { color: cum ? GREENBG : stripeFill },
        color: cum ? GREEN : pct ? GREY : BLACK,
        bold: cum || net || pct,
        align: 'left',
        valign: 'middle',
        fontFace: FONT,
        fontSize: 10.5,
      },
    };

    const valueCells = selectClientCohortDisplayValues(row.values, displayColumns).map((value) => {
      const text = fmtVal(row.kind, value);
      return {
        text,
        options: {
          fill: { color: cum ? GREENBG : oneBased % 2 ? FAINT : WHITE },
          color: cellFontColor(row.kind, Number(value) || 0, text),
          bold: cum || net,
          align: 'center',
          valign: 'middle',
          fontFace: FONT,
          fontSize: 10.5,
        },
      };
    });

    const totalText = fmtVal(row.kind, row.total);
    const totalCell = {
      text: totalText,
      options: {
        fill: { color: cum ? GREENBG : TOTCELL },
        color: cellFontColor(row.kind, Number(row.total) || 0, totalText),
        bold: true,
        align: 'center',
        valign: 'middle',
        fontFace: FONT,
        fontSize: 10.5,
      },
    };

    return [labelCell, ...valueCells, totalCell];
  });

  return [header, ...body];
}

function addCard(slide, pptx, x, y, w, h, label, value, { dark = false, valueColor } = {}) {
  slide.addShape(pptx.ShapeType.rect, {
    x,
    y,
    w,
    h,
    fill: { color: dark ? BLACK : LITE },
    ...(dark ? {} : { line: { color: LINE, width: 0.75 } }),
  });
  const labelColor = dark ? MUTE : GREY;
  const resolvedValueColor = valueColor || (dark ? WHITE : BLACK);
  slide.addText(label, {
    x: x + 0.1,
    y: y + h * 0.18,
    w: w - 0.2,
    h: h * 0.34,
    fontSize: 11,
    color: labelColor,
    align: 'center',
    valign: 'middle',
    fontFace: FONT,
  });
  slide.addText(value, {
    x: x + 0.1,
    y: y + h * 0.5,
    w: w - 0.2,
    h: h * 0.4,
    fontSize: 22,
    bold: true,
    color: resolvedValueColor,
    align: 'center',
    valign: 'middle',
    fontFace: FONT,
  });
}

function addLogo(slide, pptx, lx, ly, scale = 1.5) {
  const square = 0.085 * scale;
  const gap = 0.025 * scale;
  [
    [0, 0],
    [1, 0],
    [0, 1],
    [1, 1],
  ].forEach(([col, rowIdx]) => {
    slide.addShape(pptx.ShapeType.rect, {
      x: lx + col * (square + gap),
      y: ly + rowIdx * (square + gap),
      w: square,
      h: square,
      fill: { color: BLACK },
    });
  });
  slide.addText('TWELVE', {
    x: lx + 2 * (square + gap) + 0.06,
    y: ly - 0.06,
    w: 1.8,
    h: 0.36,
    fontSize: 16,
    bold: true,
    color: BLACK,
    fontFace: FONT,
    valign: 'middle',
  });
}

export function buildClientCohortSlidesFilename(filenameBase = 'quick-cashflow') {
  const safeName = filenameBase.replace(/[^\w\- ]+/g, '').trim() || 'quick-cashflow';
  return `${safeName}-cash-flow-with-twelve.pptx`;
}

function resolveClientCohortView({
  calcResult,
  dealIrrModel,
  dealIrrInputs,
  state,
  view,
}) {
  if (view) {
    return view;
  }
  if (dealIrrModel) {
    return buildClientCohortViewFromDealIrrModel(dealIrrModel, state, dealIrrInputs);
  }
  if (calcResult) {
    return buildClientCohortView(calcResult, state);
  }
  return null;
}

export async function buildClientCohortSlidesBlob({
  calcResult,
  dealIrrModel,
  dealIrrInputs,
  state,
  view,
  filenameBase = 'quick-cashflow',
}) {
  const built = await buildClientCohortSlidesPresentation({
    calcResult,
    dealIrrModel,
    dealIrrInputs,
    state,
    view,
    filenameBase,
  });
  if (!built) {
    return null;
  }
  const blob = await built.pptx.write({
    outputType: 'blob',
    compression: true,
  });
  return {
    blob,
    filename: built.filename,
    view: built.view,
  };
}

export async function downloadClientCohortSlides(options = {}) {
  const built = await buildClientCohortSlidesBlob(options);
  if (!built) {
    return;
  }
  saveAs(built.blob, built.filename);
}

async function buildClientCohortSlidesPresentation({
  calcResult,
  dealIrrModel,
  dealIrrInputs,
  state,
  view: prebuiltView,
  filenameBase = 'quick-cashflow',
}) {
  const view = resolveClientCohortView({
    calcResult,
    dealIrrModel,
    dealIrrInputs,
    state,
    view: prebuiltView,
  });
  if (!view) {
    return null;
  }

  const pptxgen = (await import('pptxgenjs/dist/pptxgen.es.js')).default;
  const pptx = new pptxgen();

  const W = 13.333;
  const H = 10.0;
  const Mg = 0.45;
  const UW = W - 2 * Mg;
  pptx.defineLayout({ name: 'TWELVE', width: W, height: H });
  pptx.layout = 'TWELVE';
  pptx.author = 'Twelve';
  pptx.title = 'Your Cash Flow with Twelve';

  const slide = pptx.addSlide();
  slide.background = { color: WHITE };

  // Title bar + logo
  slide.addShape(pptx.ShapeType.rect, {
    x: Mg,
    y: 0.32,
    w: UW,
    h: 0.8,
    fill: { color: WHITE },
    line: { color: 'BBBBBB', width: 1 },
  });
  slide.addText('Your Cash Flow with Twelve', {
    x: Mg + 0.12,
    y: 0.32,
    w: 9.5,
    h: 0.8,
    fontSize: 28,
    bold: true,
    color: BLACK,
    fontFace: FONT,
    valign: 'middle',
  });
  addLogo(slide, pptx, 10.95, 0.52);

  // Top KPI cards
  const fundingPctLabel = Math.round(view.topKpis.twelveFundingPct);
  const revSharePctLabel = Math.round(view.topKpis.twelveRevSharePct);
  const topCards = [
    ['Cohort marketing spend', fmtKDollar(view.topKpis.cohortMarketingSpend)],
    [`Twelve funding (${fundingPctLabel}%)`, fmtKDollar(view.topKpis.twelveFunding)],
    ['Capped return', fmtCappedReturnPct(view.topKpis.cappedReturnPct)],
  ];
  const cardGap = 0.16;
  const cardW = (UW - 2 * cardGap) / 3;
  const cardY = 1.32;
  const cardH = 1.0;
  topCards.forEach(([label, value], i) => {
    addCard(slide, pptx, Mg + i * (cardW + cardGap), cardY, cardW, cardH, label, value, { dark: true });
  });

  slide.addText('How the cohort plays out for you, month by month', {
    x: Mg,
    y: 2.42,
    w: UW,
    h: 0.34,
    fontSize: 14,
    bold: true,
    color: BLACK,
    fontFace: FONT,
    valign: 'middle',
  });

  // Table
  const tableRows = buildClientCohortSlideTableRows(view);
  const displayColumns = getClientCohortDisplayColumns(view.monthCount);
  const displayTotals = computeClientCohortDisplayTotals(view, displayColumns);
  const displayEquity = computeClientCohortDisplayEquity(view, displayTotals);
  const lastDisplayedMonth = displayColumns.length
    ? displayColumns[displayColumns.length - 1].index
    : view.monthCount - 1;
  const displayColumnCount = displayColumns.length;
  const labelColW = 2.85;
  const totalColW = 0.95;
  const monthColW = (UW - labelColW - totalColW) / Math.max(displayColumnCount, 1);
  const colW = [labelColW, ...Array(displayColumnCount).fill(monthColW), totalColW];
  const tableY = 2.84;
  const tableH = 3.66;
  const rowH = tableH / tableRows.length;

  slide.addTable(tableRows, {
    x: Mg,
    y: tableY,
    w: UW,
    colW,
    rowH,
    border: { type: 'solid', color: LINE, pt: 0.5 },
    valign: 'middle',
    fontFace: FONT,
    margin: [1, 4, 1, 6],
  });

  const supplierNote =
    view.marketingGrace === 1
      ? `Note: Month 0 is the UA month; supplier paid net-30, repayment is ${revSharePctLabel}% of cohort revenue.`
      : `Note: Month 0 is the UA month; supplier paid in cohort month, repayment is ${revSharePctLabel}% of cohort revenue.`;
  slide.addText(supplierNote, {
    x: Mg,
    y: 6.54,
    w: 8.0,
    h: 0.26,
    fontSize: 9,
    color: GREY,
    fontFace: FONT,
  });

  // Bottom band: two cards + navy ROE hero box
  const bandY = 6.96;
  const bandH = 1.3;
  const bandGap = 0.16;
  const bottomCardW = 2.7;

  const cost = view.bottomKpis.costOfFinancing;
  const costPct = Math.round(view.bottomKpis.costOfFinancingPct);
  const oopValue = view.bottomKpis.outOfPocketDay1;
  const oopText = Math.abs(oopValue) < 500 ? '$0' : fmtKDollar(oopValue);
  const oopPositive = oopText === '$0' || oopValue > 0;

  addCard(
    slide,
    pptx,
    Mg,
    bandY,
    bottomCardW,
    bandH,
    'Cost of financing',
    `$${Math.round(cost / 1000).toLocaleString('en-US')} (${costPct}%)`,
    {}
  );
  addCard(slide, pptx, Mg + (bottomCardW + bandGap), bandY, bottomCardW, bandH, 'Out of pocket on Day 1', oopText, {
    valueColor: oopPositive ? GREEN : BLACK,
  });

  const heroX = Mg + 2 * (bottomCardW + bandGap);
  const heroW = UW - (heroX - Mg);
  slide.addShape(pptx.ShapeType.rect, { x: heroX, y: bandY, w: heroW, h: bandH, fill: { color: NAVY } });
  slide.addText(`RETURN ON YOUR EQUITY  (at M${lastDisplayedMonth})`, {
    x: heroX + 0.3,
    y: bandY + 0.14,
    w: heroW - 0.6,
    h: 0.3,
    fontSize: 11,
    bold: true,
    color: 'C9D3DC',
    fontFace: FONT,
  });
  slide.addText(fmtMultiple(displayEquity.equityWithoutTwelve), {
    x: heroX + 0.3,
    y: bandY + 0.48,
    w: 2.4,
    h: 0.58,
    fontSize: 40,
    bold: true,
    color: 'B9C4CE',
    align: 'center',
    fontFace: FONT,
  });
  slide.addText('without Twelve', {
    x: heroX + 0.3,
    y: bandY + 1.05,
    w: 2.4,
    h: 0.22,
    fontSize: 10,
    color: '9AA6B0',
    align: 'center',
    fontFace: FONT,
  });
  slide.addText('→', {
    x: heroX + 2.8,
    y: bandY + 0.46,
    w: 0.7,
    h: 0.6,
    fontSize: 28,
    bold: true,
    color: WHITE,
    align: 'center',
    fontFace: FONT,
  });
  slide.addText(fmtMultiple(displayEquity.equityWithTwelve), {
    x: heroX + 3.55,
    y: bandY + 0.48,
    w: 2.4,
    h: 0.58,
    fontSize: 40,
    bold: true,
    color: '6ED38A',
    align: 'center',
    fontFace: FONT,
  });
  slide.addText('with Twelve', {
    x: heroX + 3.55,
    y: bandY + 1.05,
    w: 2.4,
    h: 0.22,
    fontSize: 10,
    bold: true,
    color: '6ED38A',
    align: 'center',
    fontFace: FONT,
  });

  slide.addText(
    `Illustrative single cohort, $ in 000s. Twelve funds ${fundingPctLabel}% of your UA the month you pay your supplier; your return on equity moves from ${fmtMultiple(
      displayEquity.equityWithoutTwelve
    )} to ${fmtMultiple(
      displayEquity.equityWithTwelve
    )}. Actual results depend on cohort performance; not a commitment by Twelve. Upload this file to Google Drive and open with Google Slides.`,
    {
      x: Mg,
      y: 8.5,
      w: UW,
      h: 0.4,
      fontSize: 9,
      color: '9A9A9A',
      fontFace: FONT,
      valign: 'top',
    }
  );

  return {
    pptx,
    view,
    filename: buildClientCohortSlidesFilename(filenameBase),
  };
}
